import {useMemo, useState} from 'react';

import {getInstanceDefaultCurrencyCode} from 'nutshell-core/session/get-instance-default-currency-code';

import {getPlanToDisplay, getAddOnTitle} from 'shells/billing/add-ons/helpers';
import {CouponPill} from 'shells/billing/billing-summary/coupon-pill';
import {PlanPrice} from 'shells/billing/plan-price';
import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {Card} from 'shells/card/card';
import {colors} from 'shells/colors/colors';
import {Stack} from 'shells/layout/stack/stack';
import {Body} from 'shells/typography/body';

import type {
    GetAvailablePlans_recurlyAvailablePlans_quotes as Quotes,
    GetAvailablePlans_recurlyAvailablePlans_crm as Crm,
    GetAvailablePlans_recurlyAvailablePlans_inbox as Engagement,
    GetAvailablePlans_recurlyAvailablePlans_marketingSuite as MarketingSuite,
    GetAccountBilling_accountBilling_currentPlan_crm_coupon as CouponType,
} from 'nutshell-graphql-types';

import type {AddOns} from '../../../setup/billing/graphql/hooks/use-get-available-plans';
import {useUpdatePlanSelection} from '../../../setup/billing/graphql/hooks/use-update-plan-selection';

import {formatCurrencyValue} from '../../../../util/format-currency-value';

import {buildActivateQuotesInput} from './helpers';

import styles from './quotes-promo-modal.module.css';

interface Props {
    onClose: () => void;
    onSuccess: () => void;
    setIsActivating: (isActivating: boolean) => void;
    quotesAddOn: Quotes | null | undefined;
    crm: Crm | null | undefined;
    engagement: Engagement | null | undefined;
    marketingSuite: MarketingSuite | null | undefined;
    addOns: AddOns | null | undefined;
}

export function QuotesPromoConfirmStep(props: Props) {
    const [error, setError] = useState<string | null>(null);

    const planToDisplay = props.quotesAddOn
        ? getPlanToDisplay(props.quotesAddOn.plans, null, props.quotesAddOn.defaultCode)
        : null;

    const {updatePlanSelection, isUpdating} = useUpdatePlanSelection();

    const mutationInput = useMemo(() => {
        if (!props.crm || !props.addOns || !props.quotesAddOn) return null;

        return buildActivateQuotesInput(
            props.crm,
            props.engagement,
            props.marketingSuite,
            props.addOns,
            props.quotesAddOn.defaultCode
        );
    }, [props.crm, props.engagement, props.marketingSuite, props.addOns, props.quotesAddOn]);

    const handleActivate = () => {
        if (!mutationInput) return;

        setError(null);
        props.setIsActivating(true);

        return updatePlanSelection(mutationInput)
            .then((result) => {
                const quotes =
                    result?.data?.planSelectionUpdate?.accountBilling?.currentPlan?.quotes;
                if (quotes) {
                    NutClientConfig.hasQuotePlan = true;
                }
                props.setIsActivating(false);
                props.onSuccess();
            })
            .catch((err: Error) => {
                setError(err.message || 'Something went wrong. Please try again.');
                props.setIsActivating(false);
            });
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles['main-content']}>
                <Body isBold={true} size='xlarge' color='dark' useDoyle={true}>
                    Review your information
                </Body>
                {planToDisplay && (
                    <Stack spacing={16}>
                        <Card hasBorder={true} fitHeight={true}>
                            <div className='flex justify-sb align-center pad-16'>
                                <Stack spacing={4}>
                                    <Body isBold={true}>Nutshell {getAddOnTitle('QUOTES')}</Body>
                                    <Body size='small' color='light'>
                                        Entire company
                                    </Body>
                                    {planToDisplay.coupon && (
                                        <CouponPill coupon={planToDisplay.coupon as CouponType} />
                                    )}
                                </Stack>
                                <Stack spacing={2}>
                                    <PlanPrice
                                        price={planToDisplay.price}
                                        stickerPrice={planToDisplay.stickerPrice}
                                        justify='end'
                                    />
                                    <Body color='dark' size='small'>
                                        per month
                                    </Body>
                                </Stack>
                            </div>
                        </Card>
                        <Card hasBorder={true} fitHeight={true} borderColor={colors.success}>
                            <div className='flex justify-sb align-center pad-16 gap-4'>
                                <Stack spacing={2}>
                                    <Body isBold={true}>Due today</Body>
                                    <Body size='small'>
                                        You will be charged ${planToDisplay.stickerPrice}/month in 3
                                        months
                                    </Body>
                                </Stack>
                                <Body isBold={true} size='xlarge' color='dark'>
                                    {formatCurrencyValue(0, getInstanceDefaultCurrencyCode())}
                                </Body>
                            </div>
                        </Card>
                    </Stack>
                )}
                {error && (
                    <Body size='small' color='alert'>
                        {error}
                    </Body>
                )}
            </div>
            <div className={styles.footer}>
                <Button variant='text-secondary' onClick={props.onClose} disabled={isUpdating}>
                    Cancel
                </Button>
                <SaveButton
                    variant='primary'
                    onClick={handleActivate}
                    disabled={!mutationInput}
                    isSaving={isUpdating}
                    errorSaving={Boolean(error)}
                    buttonText='Activate Quotes & Invoices'
                    savingText='Activating…'
                    successText='Activated!'
                    size='normal'
                />
            </div>
        </div>
    );
}
